import Advertiser from '@/common/models/advertisers/Advertiser';
import QueryParams from '@/common/models/query-params/QueryParams';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import { queryKeys } from '@/org-app/services/tanstack/queryKeys';
import { resetQueries } from '@/org-app/services/tanstack/queryUtils';
import { createGetAllAction } from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  getUniqueAdvertisers: createGetAllAction(
    API.instance.getUniqueAdvertisers,
    types.UPDATE_UNIQUE_ADVERTISERS_LIST,
    'messages.failedToGetAdvertisers',
    false,
  ),
  getAvailableUniqueAdvertisers: createGetAllAction(
    API.instance.getAvailableUniqueAdvertisers,
    types.UPDATE_AVAILABLE_ADVERTISERS_LIST,
    'messages.failedToGetAdvertisers',
    false,
  ),
  getAdvertisersWithBuyer: createGetAllAction(
    API.instance.getAdvertisersWithBuyer,
    types.UPDATE_ADVERTISERS_WITH_BUYER_LIST,
    'messages.failedToGetAdvertisers',
    false,
  ),

  async createNewAdvertiser({ commit }) {
    const advertiser = new Advertiser();
    commit(types.CREATE_ADVERTISER, advertiser);
    return advertiser;
  },
  getAllAdvertisers({ commit }) {
    return new Promise((resolve, reject) => {
      API.instance.getAdvertisers(
        (advertisers) => {
          commit(types.UPDATE_ADVERTISERS_LIST, advertisers);
          resolve(advertisers);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetAdvertisers');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getAllAdvertisersWithBuyerWithAdvertiserIDs(
    { commit },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = false } = {},
  ) {
    const advertisers = await API.instance
      .getAllAdvertisersWithBuyerWithAdvertiserIDs({ queryParams, cancelTokenSource })
      .catch((error) => {
        const msg = i18n.global.t('messages.failedToGetAdvertisers');
        ErrorHandler.showError(msg, error);
      });
    commit(types.UPDATE_ADVERTISERS_LIST, advertisers.items);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return advertisers;
  },

  async getAdvertisersAsync(
    { commit },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = false } = {},
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const advertisers = await API.instance.getAdvertisersAsync({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetAdvertisers');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });
    commit(types.UPDATE_ADVERTISERS_LIST, advertisers.items);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return advertisers;
  },

  async getTTDAdvertisersAsync(
    _,
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = false } = {},
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const advertisers = await API.instance.getTTDAdvertisersAsync({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetAdvertisers');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return advertisers;
  },

  getAdvertiser({ commit }, id) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);
      API.instance.getAdvertiser(
        id,
        (advertiser) => {
          commit(types.UPDATE_ADVERTISER, advertiser);
          resolve(advertiser);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetAdvertiser');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },
  saveAdvertiser({ commit }, advertiser) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        commit(types.SAVE_ADVERTISER, data);
        resolve(data);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSaveAdvertiser');
        ErrorHandler.showError(msg, error);
        reject(error);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };

      if (advertiser.id === -1) {
        API.instance.postAdvertiser(advertiser, successCallback, errorCallback);
      } else {
        API.instance.putAdvertiser(advertiser, successCallback, errorCallback);
      }
      resetQueries([queryKeys.advertisers]);
    });
  },
};

export default actions;
